import {useEffect, useMemo, useRef} from "react";
import {alpha, Box, Button, IconButton, Stack, ThemeProvider} from "@mui/material";
import {CloseOutlined} from "@mui/icons-material";
import Typography from "@mui/material/Typography";
import {useTranslation} from "react-i18next";
import {Link as RouterLink, useLocation} from "react-router-dom";
import CustomDialog from "../../shared/custom-dialog/CustomDialog";
import MarkdownWrapper from "../../shared/markdown-wrapper/MarkdownWrapper";
import {whiteInputsTheme} from "../../../util/theme/whiteInputsTheme";
import {RootState, useAppDispatch, useAppSelector} from "../../../store";
import {RoutePaths, StorageConstants} from "../../../util/constants";
import {togglePromoModal} from "../../../store/app/Slice";
import useHasOpenDialog from "../../../hooks/useHasOpenDialog";

const PROMO_MODAL_DELAY_MS = 3000

const PromoDialog = () => {
    const dispatch = useAppDispatch()
    const {i18n} = useTranslation()
    const {pathname} = useLocation()
    const timerRef = useRef<NodeJS.Timeout | null>(null)
    const open = useAppSelector((state: RootState) => state.appReducer.promoModalOpen)
    const wheelOpen = useAppSelector((state: RootState) => state.appReducer.spinTheWheelOpen)
    const homeLoading = useAppSelector((state: RootState) => state.homeReducer.loading)
    const promo = useAppSelector((state: RootState) => state.homeReducer.content.promo_modal)
    const hasBlockingDialog = useHasOpenDialog(["promo-dialog"])

    const promoSignature = useMemo(() => JSON.stringify({
        image: promo?.image ?? "",
        link: promo?.link ?? "",
        title_en: promo?.title_en ?? "",
        title_mk: promo?.title_mk ?? "",
        title_al: promo?.title_al ?? "",
        subtitle_en: promo?.subtitle_en ?? "",
        subtitle_mk: promo?.subtitle_mk ?? "",
        subtitle_al: promo?.subtitle_al ?? "",
    }), [promo])

    const localizedTitle = promo?.[`title_${i18n.language}`] ?? ""
    const localizedSubtitle = promo?.[`subtitle_${i18n.language}`] ?? ""
    const promoLink = promo?.link?.trim() ?? ""
    const hasPromoContent = Boolean(promo?.image && localizedTitle.trim() && localizedSubtitle.trim())
    const dismissedSignature = localStorage.getItem(StorageConstants.HOME_PROMO_DISMISSED)
    const isDismissed = dismissedSignature === promoSignature
    const canShowPromo = pathname === RoutePaths.HOME && !homeLoading && hasPromoContent && !isDismissed
    const isExternalLink = /^https?:\/\//i.test(promoLink)

    useEffect(() => {
        if (timerRef.current) {
            clearTimeout(timerRef.current)
            timerRef.current = null
        }

        if (!canShowPromo) {
            if (open) {
                dispatch(togglePromoModal(false))
            }
            return
        }

        if (wheelOpen || open || hasBlockingDialog) {
            return
        }

        timerRef.current = setTimeout(() => {
            dispatch(togglePromoModal(true))
        }, PROMO_MODAL_DELAY_MS)

        return () => {
            if (timerRef.current) {
                clearTimeout(timerRef.current)
                timerRef.current = null
            }
        }
    }, [canShowPromo, dispatch, hasBlockingDialog, open, wheelOpen])

    const handleClose = () => {
        localStorage.setItem(StorageConstants.HOME_PROMO_DISMISSED, promoSignature)
        dispatch(togglePromoModal(false))
    }

    const handlePromoLinkClick = () => {
        localStorage.setItem(StorageConstants.HOME_PROMO_DISMISSED, promoSignature)
        dispatch(togglePromoModal(false))
    }

    if (!canShowPromo) {
        return null
    }

    const dialogContent = (
        <Box position="relative">
            <Box position="relative">
                <IconButton
                    aria-label="close"
                    onClick={handleClose}
                    sx={(theme) => ({
                        position: "absolute",
                        top: {xs: 12, sm: 14},
                        right: {xs: 12, sm: 14},
                        zIndex: 2,
                        color: theme.palette.primary.main,
                        backgroundColor: alpha(theme.palette.secondary.main, 0.42),
                        backdropFilter: "blur(8px)",
                        border: `1px solid ${alpha(theme.palette.primary.main, 0.14)}`,
                        "&:hover": {
                            backgroundColor: alpha(theme.palette.secondary.main, 0.58),
                        }
                    })}
                >
                    <CloseOutlined/>
                </IconButton>

                <Box position="relative" sx={{borderRadius: 1.5, overflow: "hidden"}}>
                    <Box
                        component="img"
                        src={promo.image}
                        alt={localizedTitle}
                        sx={{
                            width: "100%",
                            minHeight: {xs: 250, sm: 300},
                            maxHeight: {xs: 330, sm: 380},
                            objectFit: "cover",
                            display: "block"
                        }}
                    />
                    <Box
                        sx={{
                            position: "absolute",
                            inset: 0,
                            background: "linear-gradient(180deg, rgba(7,6,7,0.08) 0%, rgba(7,6,7,0.16) 28%, rgba(7,6,7,0.72) 72%, rgba(7,6,7,0.96) 100%)"
                        }}
                    />
                    <Stack
                        spacing={1}
                        sx={{
                            position: "absolute",
                            left: 0,
                            right: 0,
                            bottom: 0,
                            px: {xs: 2, sm: 3},
                            pb: {xs: 2, sm: 2.5},
                        }}
                    >
                        <Typography
                            variant="h3"
                            color="primary"
                            fontWeight={700}
                            sx={{
                                maxWidth: "100%",
                                fontSize: {xs: "1.85rem", sm: "2.35rem"},
                                lineHeight: 0.98,
                                letterSpacing: "-0.03em",
                                textWrap: "balance"
                            }}
                        >
                            {localizedTitle}
                        </Typography>
                    </Stack>
                </Box>
            </Box>

            <Stack
                spacing={2}
                sx={{
                    px: 0,
                    pt: {xs: 2, sm: 2.5},
                    background: (theme) => `linear-gradient(180deg, ${alpha(theme.palette.secondary.main, 0.22)} 0%, transparent 100%)`
                }}
            >
                <MarkdownWrapper
                    source={localizedSubtitle}
                    sx={{
                        color: (theme) => alpha(theme.palette.primary.main, 0.82),
                        fontSize: {xs: "1rem", sm: "1.08rem"},
                        lineHeight: 1.5,
                        maxWidth: "54ch",
                        "& p": {
                            margin: 0,
                        },
                        "& p + p": {
                            mt: 1.25,
                        },
                        "& a": {
                            color: "inherit",
                        }
                    }}
                />
                {promoLink ? (
                    <Button
                        variant="contained"
                        color="primary"
                        size="large"
                        onClick={handlePromoLinkClick}
                        component={isExternalLink ? "a" : RouterLink}
                        href={isExternalLink ? promoLink : undefined}
                        to={isExternalLink ? undefined : promoLink}
                        target={isExternalLink ? "_blank" : undefined}
                        rel={isExternalLink ? "noopener noreferrer" : undefined}
                        sx={{alignSelf: "flex-start"}}
                    >
                        {i18n.t("learn_more")}
                    </Button>
                ) : null}
            </Stack>
        </Box>
    )

    return (
        <ThemeProvider theme={whiteInputsTheme}>
            <CustomDialog
                open={open && !hasBlockingDialog}
                maxWidth="sm"
                dialogId="promo-dialog"
                handleDialogClose={handleClose}
                content={dialogContent}
                contentSx={{p: 0}}
                sx={(theme) => ({
                    overflow: "hidden",
                    borderColor: alpha(theme.palette.primary.main, 0.18),
                    backgroundColor: alpha(theme.palette.secondary.main, 0.88),
                    backgroundImage: `linear-gradient(180deg, ${alpha(theme.palette.quaternary.main, 0.16)} 0%, transparent 40%)`,
                    boxShadow: `0 32px 80px ${alpha(theme.palette.secondary.main, 0.58)}`
                })}
            />
        </ThemeProvider>
    )
}

export default PromoDialog
